import { FullHeightPage } from '@client-info/features/shared/FullHeightPage';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography, { type TypographyProps } from '@mui/material/Typography';

// CSS Variables - Define defaults
const getCssVariables = () => ({
  '--max-description-width': '62rem',
  '--max-total-width': '100rem',
  '--spacing-small': '8px',
  '--padding-container': '2em',
  '--padding-mobile': '1em',
  '--table-padding': '16px 8px',
  '--table-mobile-padding': '8px',
});

export const FullHeightContainer = styled(FullHeightPage)(() => ({
  alignItems: 'center',
  fontSize: '0.8rem',
  ...getCssVariables(),
}));

export const CookieConsentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 'var(--max-total-width)',
  padding: 'var(--padding-container)',
  marginBlock: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: 'var(--padding-mobile)',
  },
}));

const HeadingComponent = (props: TypographyProps) => (
  <Typography variant="h4" component="h1" {...props} />
);

export const CookieHeading = styled(HeadingComponent)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('xs')]: {
    fontSize: '1.8em',
    marginBottom: theme.spacing(2),
  },
}));

export const CookieDescription = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8em',
  },
}));

const SubHeadingComponent = (props: TypographyProps) => (
  <Typography variant="subtitle1" component="h2" {...props} />
);

export const CookieSubheading = styled(SubHeadingComponent)({
  fontWeight: 600,
  textTransform: 'capitalize',
  margin: 0,
});

const DescriptionComponent = (props: TypographyProps) => (
  <Typography variant="body1" {...props} />
);
export const CookieText = styled(DescriptionComponent)(({ theme }) => ({
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: 400,
  maxWidth: 'var(--max-description-width)',
  marginBottom: theme.spacing(4),
}));

export const CookieTable = styled('table')(({ theme }) => {
  const palette = theme.vars?.palette || theme.palette;
  return {
    width: '100%',
    boxSizing: 'border-box',
    borderCollapse: 'collapse',
    border: `1px solid ${palette.divider}`,

    '& thead': {
      backgroundColor: palette.grey[50],

      '& tr': {
        borderBottom: `1px solid ${palette.divider}`,
      },

      '& th': {
        padding: 'var(--table-padding)',
        textAlign: 'left',
        fontSize: theme.typography.subtitle2.fontSize,
        borderRight: `1px solid ${palette.divider}`,
        maxWidth: '5em',

        '&:last-child': {
          borderRight: 'none',
        },
      },
    },

    '& tbody': {
      '& tr': {
        borderBottom: `1px solid ${palette.divider}`,
      },

      '& td': {
        padding: 'var(--table-padding)',
        fontSize: theme.typography.body1.fontSize,
        borderRight: `1px solid ${palette.divider}`,
        verticalAlign: 'top',
        maxWidth: '5em',

        '&:last-child': {
          borderRight: 'none',
        },
      },
    },

    [theme.breakpoints.down('sm')]: {
      width: '100%',
      border: 'none',
      borderSpacing: 0,

      '& thead': {
        display: 'none',
      },

      '& tbody': {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        width: '100%',
        boxSizing: 'border-box',

        '& tr': {
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          boxSizing: 'border-box',
          padding: 'var(--table-mobile-padding)',
          border: `1px solid ${palette.divider}`,
          borderBottom: `1px solid ${palette.divider}`,

          '&:last-child': {
            marginBottom: 0,
          },
        },

        '& td': {
          boxSizing: 'border-box',
          border: 'none',
          borderRight: 'none',
          textAlign: 'left',
          minWidth: '100%',
          padding: 0,
          marginBottom: 'var(--spacing-small)',

          '&::before': {
            content: 'attr(data-label)',
            fontWeight: 600,
            display: 'block',
            marginBottom: '2px',
          },

          '&:last-child': {
            border: 'none',
            margin: 0,
          },
        },
      },
    },
  };
});
